import { DEFAULT_CALLBACK_SECONDS } from "@/lib/dispositions";
import type { Disposition, LeadCallback } from "@/lib/types";

/**
 * A callback at the time the rep picked (ADR-080): the lead's wall clock to an instant and back,
 * the time a picker opens on, what is wrong with a time, and the words a callback is said in.
 *
 * The rep picks on the LEAD's clock. "Call me back at three" is the lead's three, and it is the
 * clock the dialer calls them on: `clock_timezone` on the card — their own zone, else their state's,
 * else the calling campaign's. The time goes to core-api as an instant with that zone's offset. With
 * no zone known the picker falls back to the rep's own clock, and says so.
 */

/** How far ahead core-api takes a callback (`callbacks.HORIZON`). */
export const CALLBACK_HORIZON_DAYS = 90;
/** The hours a campaign may call a lead in at the widest, on their clock: 8 AM to 9 PM. */
export const CALLING_HOURS = { from: 8, to: 21 } as const;

const MINUTE = 60_000;
const QUARTER = 15 * MINUTE;
const DAY = 86_400_000;

/** A wall clock: the date and the time a person reads, in no zone of its own. */
export interface WallTime {
  /** YYYY-MM-DD, what `<input type="date">` holds. */
  date: string;
  /** HH:mm (24 h), what `<input type="time">` holds. */
  time: string;
}

/** The browser's own zone: the rep's clock. */
export function ownZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** A zone this browser knows, or null. */
export function knownZone(zone: string | null | undefined): string | null {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function parts(instant: number, zone: string): Parts {
  const out: Parts = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  for (const p of fmt.formatToParts(instant)) {
    if (!(p.type in out)) continue;
    const n = Number(p.value);
    // Some ICU builds say midnight as hour 24 even with h23.
    out[p.type as keyof Parts] = p.type === "hour" ? n % 24 : n;
  }
  return out;
}

/** How far `zone`'s clock is ahead of UTC at `instant`, in milliseconds (-7 h is negative). */
export function offsetAt(instant: number, zone: string): number {
  const p = parts(instant, zone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The wall clock `zone` shows at `instant`. */
export function wallTime(instant: number, zone: string): WallTime {
  const p = parts(instant, zone);
  return { date: `${p.year}-${pad(p.month)}-${pad(p.day)}`, time: `${pad(p.hour)}:${pad(p.minute)}` };
}

/** The instant a wall clock names in `zone`; null for a date or time that is not one. A time the
 *  clocks skip in spring, or show twice in autumn, is read as the zone's offset just before it. */
export function instantOf(wall: WallTime, zone: string): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wall.date);
  const t = /^(\d{2}):(\d{2})$/.exec(wall.time);
  if (!d || !t) return null;
  const guess = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  if (Number.isNaN(guess)) return null;
  const first = guess - offsetAt(guess, zone);
  return guess - offsetAt(first, zone);
}

/** RFC 3339 with the zone's own offset, as core-api is sent a callback: "2026-09-30T15:00:00-07:00". */
export function isoWithOffset(instant: number, zone: string): string {
  const w = wallTime(instant, zone);
  const minutes = Math.round(offsetAt(instant, zone) / MINUTE);
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${w.date}T${w.time}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** Up to the next quarter hour: a callback is promised for 3:15, not 3:07. */
export function nextQuarter(instant: number): number {
  return Math.ceil(instant / QUARTER) * QUARTER;
}

/** Where a picker opens: the outcome's own wait from now — what a callback given no time does —
 *  up to the next quarter hour. */
export function defaultCallback(outcome: Pick<Disposition, "backoff_seconds">, now: number): number {
  return nextQuarter(now + (outcome.backoff_seconds ?? DEFAULT_CALLBACK_SECONDS) * 1000);
}

/** The times a rep says most, on the lead's clock: in an hour, in three, tomorrow morning and
 *  afternoon. */
export function quickPicks(now: number, zone: string): { label: string; instant: number }[] {
  const tomorrow = wallTime(instantOf(wallTime(now, zone), zone)! + DAY, zone).date;
  const at = (time: string) => instantOf({ date: tomorrow, time }, zone)!;
  return [
    { label: "In 1 hour", instant: nextQuarter(now + 60 * MINUTE) },
    { label: "In 3 hours", instant: nextQuarter(now + 180 * MINUTE) },
    { label: "Tomorrow 10 AM", instant: at("10:00") },
    { label: "Tomorrow 2 PM", instant: at("14:00") },
  ];
}

/** What stops a callback at this time, in core-api's words: none picked, one that has passed (a
 *  minute of grace for the clock), one past the horizon. Null when it may be saved. */
export function callbackProblem(instant: number | null, now: number): string | null {
  if (instant === null) return "Pick a date and a time.";
  if (instant < now - MINUTE) return "That time has passed: pick a time from now on.";
  if (instant > now + CALLBACK_HORIZON_DAYS * DAY) return `A callback can be at most ${CALLBACK_HORIZON_DAYS} days ahead.`;
  return null;
}

/** Whether the time falls outside the hours any campaign may call the lead in, on their clock. The
 *  dialer holds the call until the hours open, so this is a warning, not a refusal. */
export function outsideCallingHours(instant: number, zone: string): boolean {
  const { hour } = parts(instant, zone);
  return hour < CALLING_HOURS.from || hour >= CALLING_HOURS.to;
}

/** "PDT", "CST", or "GMT+5" where the zone has no letters. */
export function zoneName(instant: number, zone: string): string {
  const found = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" })
    .formatToParts(instant)
    .find((p) => p.type === "timeZoneName");
  return found?.value ?? zone;
}

/** "Tue, Sep 30, 3:00 PM" on `zone`'s clock. */
export function whenText(instant: number | string, zone: string): string {
  const at = typeof instant === "string" ? Date.parse(instant) : instant;
  // Newer ICU puts a narrow no-break space before "PM"; the rest of the console uses a plain one.
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(at)
    .replaceAll(" ", " ");
}

/** A callback's time as the record says it: on the lead's clock with its zone, and the reader's
 *  own beside it when the two differ. */
export function callbackWhen(
  dueAt: string | number,
  leadZone: string | null,
  viewerZone: string = ownZone(),
): { there: string; yours: string | null } {
  const at = typeof dueAt === "string" ? Date.parse(dueAt) : dueAt;
  const zone = knownZone(leadZone);
  if (!zone) return { there: `${whenText(at, viewerZone)} your time`, yours: null };
  const there = `${whenText(at, zone)} ${zoneName(at, zone)}`;
  const same = offsetAt(at, zone) === offsetAt(at, viewerZone);
  return { there, yours: same ? null : `${whenText(at, viewerZone)} your time` };
}

/** "in 2 hours", "10 minutes ago". */
function inWords(at: number, now: number): string {
  const seconds = Math.round((at - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [["day", 86_400], ["hour", 3_600], ["minute", 60]];
  for (const [unit, per] of units) {
    if (Math.abs(seconds) >= per) return rtf.format(Math.round(seconds / per), unit);
  }
  return "now";
}

export type CallbackTone = "accent" | "warning" | "neutral" | "danger";

/** Where a callback stands, as a badge says it (`callbacks.STATUSES` in core-api). */
export function callbackStatus(
  cb: Pick<LeadCallback, "status" | "due_at">,
  now: number,
): { label: string; tone: CallbackTone; detail: string | null } {
  const due = Date.parse(cb.due_at);
  switch (cb.status) {
    case "upcoming":
      return { label: "Upcoming", tone: "accent", detail: inWords(due, now) };
    case "due":
      return { label: "Due", tone: "warning", detail: now - due < MINUTE ? "now" : inWords(due, now) };
    case "done":
      return { label: "Done", tone: "neutral", detail: null };
    case "rescheduled":
      return { label: "Rescheduled", tone: "neutral", detail: null };
    case "cancelled":
      return { label: "Cancelled", tone: "danger", detail: "the lead has left" };
    default:
      return { label: cb.status, tone: "neutral", detail: null };
  }
}
